import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { Megaphone, Upload, X, ArrowLeft } from 'lucide-react';

const MAX_FILES = 5;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

const AnnouncementCreate = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [visibility, setVisibility] = useState('staff');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');

  const createMutation = useMutation(
    (formData) => api.post('/announcements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    {
      onSuccess: () => {
        toast.success('Circular published successfully!');
        navigate('/announcements');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to create circular');
      }
    }
  );

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFileError('');

    const invalid = selected.filter(f => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length > 0) {
      setFileError('Only PDF, JPG, JPEG, and PNG files are allowed');
      return;
    }

    const total = files.length + selected.length;
    if (total > MAX_FILES) {
      setFileError(`Maximum ${MAX_FILES} attachments allowed`);
      return;
    }

    setFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      return;
    }

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('message', message.trim());
    formData.append('visibility', visibility);
    files.forEach(f => formData.append('documents', f));

    createMutation.mutate(formData);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/announcements')}
          className="p-1.5 text-secondary-400 hover:text-secondary-600 rounded"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">New Circular</h1>
          <p className="text-sm text-secondary-500 mt-0.5">Issue a new circular or announcement</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-secondary-200 p-6 space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Land permit office hours update"
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Enter the circular content..."
            rows={6}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
            required
          />
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Visibility <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={() => setVisibility('public')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-secondary-900">Public</span>
                <p className="text-xs text-secondary-500">Visible on the home page and to all users</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="visibility"
                value="staff"
                checked={visibility === 'staff'}
                onChange={() => setVisibility('staff')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-secondary-900">Staff Only</span>
                <p className="text-xs text-secondary-500">Visible only to logged-in portal users</p>
              </div>
            </label>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Attachments <span className="text-secondary-400 font-normal">(optional, max {MAX_FILES})</span>
          </label>
          <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors w-fit">
            <Upload className="h-4 w-4 text-secondary-400" />
            <span className="text-sm text-secondary-600">Choose files (PDF, JPG, PNG)</span>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              disabled={files.length >= MAX_FILES}
            />
          </label>

          {fileError && (
            <p className="text-xs text-red-600 mt-1">{fileError}</p>
          )}

          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-secondary-50 rounded px-3 py-1.5">
                  <Megaphone className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
                  <span className="text-sm text-secondary-700 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-secondary-400">{formatFileSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-secondary-400 hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={createMutation.isLoading || !title.trim() || !message.trim()}
            className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isLoading ? 'Publishing...' : 'Publish Circular'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/announcements')}
            className="px-5 py-2 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-medium hover:bg-secondary-200"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AnnouncementCreate;
